<script>
	import { LayerCake, Svg, ScaledSvg, uniques, Html } from "layercake";
	import { fade, blur, fly, slide, scale } from "svelte/transition";
	import { scaleOrdinal, scaleTime } from "d3-scale";

	import AxisX from "../components/AxisX.svelte";
	import AxisY from "../components/AxisY.svelte";
	import Line from "../components/Line.svelte";
	import Area from "../components/Area.svelte";
	import MultiLine from "../components/MultiLine.svelte";
	import SharedTooltip from "../components/SharedTooltip.svelte";
	import GroupLabels from "../components/GroupLabels.svelte";
	import IndicatorX from "../components/IndicatorX.svelte";
	import Highlight from "../components/Highlight.svelte";
	import LowestPoint from "../components/LowestPoint.svelte";
	import LowestPointPrevious from "../components/LowestPointPrevious.svelte";

	import {
		formatMonth,
		formatYear,
		formatOnlyMonth,
		formatOnlyMonthFull,
		deepcopy,
		formatFloat,
	} from "../utils";
	import { months, nonValueKeys, colorsIncoming, results, gasStopIndicator } from "../stores";

	export let data;
	export let dataCompare;
	export let colors = ["#ffe4b8", "#ffb3c0", "#ff7ac7", "#ff00cc"];
	export let yTicks = [];
	export let min = null;
	export let max = null;
	export let range = [];
	export let title = "";
	export let subtitle = "";
	export let titleIcon = "";
	export let subtitleIcon = "";
	export let result = "neutral";
	export let showIndicator = false;

	const xKey = "date";
	const yKey = "value";
	const zKey = "variable";

	const frmtF = formatFloat(0);

	const heizperiode = {
		startMonth: 9,
		startYear: 2022,
		endMonth: 4,
		endYear: 2023,
	};
	heizperiode.startDate = new Date(heizperiode.startYear, heizperiode.startMonth);
	heizperiode.endDate = new Date(heizperiode.endYear, heizperiode.endMonth);

	$: seriesNames = Object.keys(data[0]).filter((d) => d !== xKey && !$nonValueKeys.includes(d));
	// const seriesColors = ["#ffe4b8", "#ffb3c0", "#ff7ac7", "#ff00cc"];
	$: seriesColors = colors;
	$: mainColor = colors[0];

	/* --------------------------------------------
	 * Create a "long" format that is a grouped series of data points
	 * Layer Cake uses this data structure and the key names
	 * set in xKey, yKey and zKey to map your data into each scale.
	 */
	$: dataLong = seriesNames.map((key) => {
		return {
			[zKey]: key,
			values: data.map((d) => {
				// d[xKey] = typeof d[xKey] === "string" ? parseDate(d[xKey]) : d[xKey]; // Conditional required for sapper
				return {
					[yKey]: +d[key],
					[xKey]: d[xKey],
				};
			}),
		};
	});

	/* --------------------------------------------
	 * Make a flat array of the `values` of our nested series
	 * we can pluck the field set from `yKey` from each item
	 * in the array to measure the full extents
	 */
	$: flatten = (data) =>
		data.reduce((memo, group) => {
			return memo.concat(group.values);
		}, []);

	const minCompare = Math.min(...dataCompare.map((d) => d.value));
	const minCompareData = dataCompare.filter((d) => d.value === minCompare)[0];
</script>

<div class="heading" transition:fade>
	<span class="title" style:color={mainColor}>
		Gasverfügbarkeit laut ausgewähltem

		<div class="icon">
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.46 283.46"
				><polyline
					style:stroke={mainColor}
					points="56.69 148.82 99.21 120.47 184.25 177.17 240.94 141.73"
				/></svg
			>
		</div>
		Szenario</span
	><br /><span class="subtitle"
		>im Vergleich zu <b
			>allen berechneten Szenarien<img src={`${PATH}images/graph_range.svg`} class="icon" /></b
		>
		<!-- <br />
		und des bereits ungewöhnlich gasarmen
		<b>Jahres 21/22<img src={`${PATH}images/graph_mittelwert.svg`} class="icon" /></b></span> -->
	</span>
</div>
<div class="chart">
	<LayerCake
		ssr={true}
		percentRange={true}
		padding={{ top: 7, right: 0, bottom: 20, left: 0 }}
		x={xKey}
		y={yKey}
		z={zKey}
		xScale={scaleTime()}
		zScale={scaleOrdinal()}
		zDomain={seriesNames}
		zRange={seriesColors}
		flatData={flatten(dataLong)}
		yDomain={[min, max]}
		data={dataLong}
	>
		<Html>
			<AxisY
				gridlines={true}
				baseline={false}
				ticks={yTicks}
				formatTick={(t, i) => (i === yTicks.length - 1 ? `TWh<br>${t}` : t)}
			/>
			<AxisX
				gridlines={false}
				baseline={true}
				formatTick={(d, i) =>
					(d.getMonth() === heizperiode.startMonth && d.getFullYear() === heizperiode.startYear) ||
					(d.getMonth() === heizperiode.endMonth && d.getFullYear() === heizperiode.endYear)
						? `${formatOnlyMonth(d)} ${formatYear(d)}`
						: ""}
				tickMarks={true}
				yTick={-2}
			/>
			<Highlight
				start={heizperiode.startDate}
				end={heizperiode.endDate}
				label="Heizperiode"
				color={mainColor}
			/>
		</Html>
		<ScaledSvg>
			<Area data={range} opacity={0.03} fill={"f5f5f5"} />
			<!-- <MultiLine dataE={dataCompare} strokeWidth={1} /> -->
			<MultiLine strokeWidth={3} />
		</ScaledSvg>
		<Html>
			{#if $gasStopIndicator}
				<IndicatorX color={$colorsIncoming.main} label={"russischer<br>Lieferstopp"} />
			{/if}
			<LowestPoint prefix="Tiefpunkt:" format={(d) => formatOnlyMonthFull(d.date)} />
			<!-- <LowestPointPrevious prefix="Tiefpunkt 21/22" data={minCompareData} /> -->
			<SharedTooltip
				dataset={data}
				{range}
				color={mainColor}
				lineColor={"#c9c9c9"}
				formatTitle={(d) => `${formatOnlyMonth(d)} ${formatYear(d)}`}
				formatValue={(d) => `${frmtF(d)} TWh`}
				shownKeys={Object.keys(data[0]).filter((k) => !$nonValueKeys.includes(k))}
			/>
		</Html>
	</LayerCake>
</div>

<style lang="scss">
	.chart {
		height: 163px;
	}

	.heading {
		line-height: 11px;
		letter-spacing: 0.01em;
		z-index: 2;
		margin-bottom: 7px;
	}

	@media (min-width: 840px) {
		.title {
			font-size: 15px !important;
			// line-height: 14px;
			line-height: 16px !important;

			.icon {
				margin-top: 0px;
			}
		}

		.subtitle {
			font-size: 11.5px !important;
			line-height: 11px !important;
		}
	}

	.icon {
		// margin: 0 4px 4px 0;
		// vertical-align: middle;
		vertical-align: text-top;
		max-height: 16px;
		height: 16px;
		width: 16px;
		display: inline-block;
		padding-right: 2px;
		margin-top: -2px;
		display: inline-block !important;
	}

	.title {
		font-weight: 600;
		// font-size: 11.5px;
		font-size: 13px;
		line-height: 14px;
		transition: color 1s ease-in;
		letter-spacing: 0.01em;

		.icon {
			padding-right: 2px;
		}
	}

	.subtitle {
		font-size: 11.5px;
		line-height: 11px;
		opacity: 0.6;
		font-weight: 500;
		color: #797979;
		letter-spacing: 0.02em;

		b {
			white-space: nowrap;
		}

		.icon {
			padding-right: 1px;
		}
	}

	.icon svg polyline {
		transition: stroke 1s ease-in;
		fill: transparent;
		stroke: transparent;
		stroke-linecap: round;
		stroke-miterlimit: 10;
		stroke-width: 44px;
	}
</style>
